import { useMemo } from "react";
import { Building2, CalendarRange, Filter, RefreshCcw, Sparkles } from "lucide-react";
import SalviaLayout from "@/components/SalviaLayout";
import ModuleAIChatWidget from "@/components/ModuleAIChatWidget";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { useHealthFilters } from "@/hooks/useHealthFilters";
import { buildAIContextSnapshot } from "@/lib/aiScopeContext";
import ApsModulePanels from "@/components/ApsModulePanels";
import TelemedicineModulePanels from "@/components/TelemedicineModulePanels";
import { useUI } from "@/contexts/UIContext";
import { useAuth } from "@/_core/hooks/useAuth";
import { getModuleAccessByKey } from "@/lib/portalShell";

type HealthServiceModuleDashboardProps = {
  title: string;
  eyebrow: string;
  description: string;
  serviceKey: string;
  accentColor: string;
  gradientClassName: string;
  badgeLabel: string;
  focusTitle: string;
  focusItems: Array<{
    title: string;
    description: string;
  }>;
};

type ApsSection = {
  id: string;
  label: string;
  description: string;
};

const ADMIN_APS_SECTION: ApsSection = {
  id: "avaliacao-global",
  label: "Avaliação global",
  description: "Leitura consolidada do admin para comparar canal, empresa, produção e risco em escala global.",
};

const BASE_APS_SECTIONS: ApsSection[] = [
  {
    id: "visao-geral",
    label: "Visão geral",
    description: "Resumo executivo com base, risco, busca ativa, monitoramento e experiência.",
  },
  {
    id: "populacao",
    label: "População",
    description: "Leitura da carteira elegível, pirâmide etária e composição demográfica.",
  },
  {
    id: "producao",
    label: "Dashboards",
    description: "Produção, funil de acesso e ranking assistencial por especialidade.",
  },
  {
    id: "busca-ativa",
    label: "Busca ativa",
    description: "Campanhas, segmentos, canais e conversão operacional em agenda.",
  },
  {
    id: "monitoramento",
    label: "Monitoramento",
    description: "Pronto-socorro, telemonitoramento e desfechos por prestador.",
  },
  {
    id: "saude-populacional",
    label: "Saúde populacional",
    description: "Risco, estilo de vida, rastreamentos e linhas prioritárias de cuidado.",
  },
  {
    id: "experiencia",
    label: "Insights",
    description: "NPS, sentimentos, temas e depoimentos recentes do beneficiário.",
  },
  {
    id: "acoes-coletivas",
    label: "Ações coletivas",
    description: "Acompanhamento de ações educativas, engajamento e participação.",
  },
];

function formatLongDate(value?: string | Date | null) {
  if (!value) return "Sem atualização disponível";

  const date = value instanceof Date
    ? value
    : new Date(value.includes("T") ? value : `${value}T12:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return "Sem atualização disponível";
  }

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function toNumber(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isFilledOverview(data: any) {
  return (
    toNumber(data?.population?.eligibleLives) > 0 ||
    toNumber(data?.population?.activeUsers) > 0 ||
    toNumber(data?.access?.encountersTotal) > 0 ||
    toNumber(data?.experience?.avgScore) > 0 ||
    toNumber(data?.risk?.livesWithSignal) > 0
  );
}

function summarizeScope(scopeType?: string | null) {
  if (scopeType === "global") return "Leitura global com filtros por canal e empresa liberados para o admin.";
  if (scopeType === "channel") return "Leitura governada pelo canal do usuário, com foco nas empresas vinculadas ao seu escopo.";
  if (scopeType === "company") return "Leitura restrita à empresa do usuário, preservando integralmente o contexto do cliente.";
  return "Leitura protegida conforme o perfil autenticado no portal.";
}

function summarizeCompactScope(scopeType?: string | null) {
  if (scopeType === "global") return "Escopo global habilitado.";
  if (scopeType === "channel") return "Escopo restrito ao canal.";
  if (scopeType === "company") return "Escopo protegido da empresa.";
  return "Escopo protegido pelo perfil.";
}

function resolveSharedSectionLabel(activeSectionId: string) {
  if (activeSectionId === "producao") return "Produção médica";
  if (activeSectionId === "eficiencia") return "Eficiência enfermagem";
  if (activeSectionId === "no-show") return "No-show";
  return "Visão geral";
}

function resolveHealthRoute(serviceKey: string) {
  if (serviceKey === "aps_fisica") return "/portal/servicos/aps-fisica";
  if (serviceKey === "aps_digital") return "/portal/servicos/aps-digital";
  if (serviceKey === "telemedicina") return "/portal/servicos/telemedicina";
  return "/portal/salvia";
}

function hasHealthAssistantAccess(workspace: any) {
  const accessEnvelope = workspace?.accessEnvelope;
  return accessEnvelope?.isAdminBypass === true
    || accessEnvelope?.widget?.contractEnabled === true
    || accessEnvelope?.hub?.contractEnabled === true;
}

export default function HealthServiceModuleDashboard({
  title,
  eyebrow,
  description,
  serviceKey,
  accentColor,
  gradientClassName,
  badgeLabel,
  focusTitle,
  focusItems,
}: HealthServiceModuleDashboardProps) {
  const isApsModule = serviceKey === "aps_fisica" || serviceKey === "aps_digital";
  const isTelemedicineModule = serviceKey === "telemedicina";
  const { isExplicativo } = useUI();
  const { user } = useAuth();
  const platformNucleus = (user as any)?.platformNucleus;
  const moduleAccess = Array.isArray(platformNucleus?.modules) ? platformNucleus.modules : [];
  const currentModuleAccess = getModuleAccessByKey(moduleAccess, serviceKey);
  const hasVisibleModuleAccess = Boolean(
    currentModuleAccess?.userHasVisibleAccess
      || currentModuleAccess?.userHasOperationalAccess
      || currentModuleAccess?.visibilityState === "visible"
      || currentModuleAccess?.visibilityState === "operable",
  );
  const isVisibleReadOnly = hasVisibleModuleAccess && currentModuleAccess?.userHasOperationalAccess === false;

  const {
    filters,
    periodPreset,
    setPeriodPreset,
    careModality,
    setCareModality,
    companyId,
    setCompanyId,
    channelId,
    setChannelId,
    activeSectionId,
    setActiveSectionId,
    resetFilters,
  } = useHealthFilters({
    defaultServiceKey: serviceKey,
    defaultSectionId: "visao-geral",
  });

  const contextQuery = trpc.healthIndicators.context.useQuery(filters, {
    enabled: !isApsModule,
    refetchOnWindowFocus: false,
  });
  const overviewQuery = trpc.healthIndicators.overview.useQuery(filters, {
    enabled: !isApsModule,
    refetchOnWindowFocus: false,
  });
  const timelineQuery = trpc.healthIndicators.timeline.useQuery(filters, {
    enabled: !isApsModule,
    refetchOnWindowFocus: false,
  });
  const breakdownsQuery = trpc.healthIndicators.breakdowns.useQuery(filters, {
    enabled: !isApsModule,
    refetchOnWindowFocus: false,
  });

  const apsFilters = useMemo<{
    from?: string;
    to?: string;
    serviceKey?: "aps_fisica" | "aps_digital";
    companyId?: number;
    channelId?: number;
  }>(
    () => ({
      from: filters.from,
      to: filters.to,
      serviceKey: serviceKey === "aps_fisica" || serviceKey === "aps_digital" ? serviceKey : undefined,
      companyId,
      channelId,
    }),
    [channelId, companyId, filters.from, filters.to, serviceKey],
  );

  const apsContextQuery = trpc.apsIndicators.context.useQuery(apsFilters, {
    enabled: isApsModule,
    refetchOnWindowFocus: false,
  });
  const apsDashboardQuery = trpc.apsIndicators.dashboard.useQuery({ ...apsFilters, limit: 8 }, {
    enabled: isApsModule,
    refetchOnWindowFocus: false,
  });

  const aiContextSnapshot = useMemo(
    () => buildAIContextSnapshot(typeof window === "undefined" ? resolveHealthRoute(serviceKey) : window.location.pathname),
    [serviceKey],
  );

  const healthAiAccessQuery = trpc.insights.getWorkspaceContext.useQuery(
    {
      surface: "widget",
      contextSnapshot: aiContextSnapshot,
    },
    {
      staleTime: 60_000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const error = isApsModule
    ? apsContextQuery.error ?? apsDashboardQuery.error
    : contextQuery.error ?? overviewQuery.error ?? timelineQuery.error ?? breakdownsQuery.error;

  const isLoading = isApsModule
    ? (apsContextQuery.isLoading && !apsContextQuery.data) || (apsDashboardQuery.isLoading && !apsDashboardQuery.data)
    : (contextQuery.isLoading && !contextQuery.data) ||
      (overviewQuery.isLoading && !overviewQuery.data) ||
      (timelineQuery.isLoading && !timelineQuery.data) ||
      (breakdownsQuery.isLoading && !breakdownsQuery.data);

  const overview = overviewQuery.data;
  const context = contextQuery.data;
  const breakdowns = breakdownsQuery.data;
  const apsContext = apsContextQuery.data;
  const apsDashboard = apsDashboardQuery.data;

  const hasOverviewData = isFilledOverview(overview);
  const hasTimelineData = (timelineQuery.data ?? []).some(
    (row) =>
      toNumber(row.encountersTotal) > 0 ||
      toNumber(row.appointmentsTotal) > 0 ||
      toNumber(row.avgScore) > 0,
  );
  const hasBreakdownData =
    (breakdowns?.specialties ?? []).length > 0 ||
    (breakdowns?.services ?? []).length > 0 ||
    (breakdowns?.riskDomains ?? []).length > 0;
  const hasAnyData = hasOverviewData || hasTimelineData || hasBreakdownData;

  const modalityOptions = context?.availableCareModalities ?? [];
  const apsScopeType = typeof apsContext?.scope?.type === "string" ? apsContext.scope.type : null;
  const showTelemedicineOverview = !isTelemedicineModule || activeSectionId === "visao-geral";
  const showTelemedicineTimeline = !isTelemedicineModule || activeSectionId === "visao-geral" || activeSectionId === "producao" || activeSectionId === "no-show";
  const showTelemedicineBreakdowns = !isTelemedicineModule || activeSectionId === "visao-geral" || activeSectionId === "producao" || activeSectionId === "eficiencia";
  const channelOptions = Array.isArray(apsContext?.channels) ? apsContext.channels : [];
  const companyOptions = Array.isArray(apsContext?.companies) ? apsContext.companies : [];
  const sharedCompanyOptions = Array.isArray(context?.companies) ? context.companies : [];
  const availableApsSections = apsScopeType === "global"
    ? [ADMIN_APS_SECTION, ...BASE_APS_SECTIONS]
    : BASE_APS_SECTIONS;
  const selectedSection = availableApsSections.find((section) => section.id === activeSectionId) ?? availableApsSections[0];
  const selectedSharedSectionLabel = resolveSharedSectionLabel(activeSectionId);
  const selectedSectionLabel = isApsModule ? selectedSection.label : selectedSharedSectionLabel;
  const focusCards = isExplicativo ? focusItems : focusItems.slice(0, 2);
  const compactDescription = isApsModule
    ? `${title} em leitura executiva, com foco em produção, risco, experiência e contexto protegido do cliente.`
    : `${title} em leitura executiva, com foco em produção, eficiência, risco e experiência no período.`;
  const sharedRole = typeof context?.scope?.role === "string" ? context.scope.role : null;
  const showSharedCompanyFilter = !isApsModule && sharedCompanyOptions.length > 1 && sharedRole !== "client_rh";
  const latestConsolidation = overview?.latestMetricDate instanceof Date || typeof overview?.latestMetricDate === "string"
    ? overview.latestMetricDate
    : context?.dateBounds?.lastPopulationDate instanceof Date || typeof context?.dateBounds?.lastPopulationDate === "string"
      ? context.dateBounds.lastPopulationDate
      : filters.to ?? null;
  const periodLabel = periodPreset === "7d" ? "Últimos 7 dias" : periodPreset === "30d" ? "Últimos 30 dias" : "Últimos 90 dias";
  const heroCards = [
    {
      title: "Escopo",
      value: isApsModule ? companyOptions.length : context?.scopedCompanies ?? 0,
      description: isApsModule ? summarizeCompactScope(apsScopeType) : "Leitura governada pelo perfil autenticado.",
      icon: Building2,
    },
    {
      title: "Atualização",
      value: formatLongDate(latestConsolidation),
      description: "Base consolidada no recorte atual.",
      icon: CalendarRange,
    },
    {
      title: "Período",
      value: periodLabel,
      description: `Seção ativa: ${selectedSectionLabel}.`,
      icon: Sparkles,
    },
  ];
  const showHealthAssistant = hasHealthAssistantAccess(healthAiAccessQuery.data);

  return (
    <SalviaLayout title={title}>
      <div className="space-y-6">
        <section className={`overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white/94 shadow-sm ${gradientClassName}`}>
          <div className="border-b border-slate-200/80 bg-white/94 px-4 py-4 md:px-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="outline" className="border-white/60 bg-white/75 text-slate-700">
                    {badgeLabel}
                  </Badge>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{eyebrow}</span>
                </div>
                <h1 className="mt-3 text-[1.7rem] font-bold tracking-tight text-slate-950 md:text-[1.95rem]">{title}</h1>
                <p className="mt-1.5 max-w-[52rem] text-sm leading-6 text-slate-700">{compactDescription}</p>
              </div>

              <div className="grid gap-2 sm:grid-cols-3 xl:min-w-[420px]">
                {heroCards.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="rounded-[1.15rem] border border-slate-200 bg-white px-3.5 py-3 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{item.title}</p>
                          <p className="mt-1 text-sm font-semibold text-slate-950 md:text-[0.98rem]">{item.value}</p>
                        </div>
                        <div className="rounded-xl p-2" style={{ backgroundColor: `${accentColor}14` }}>
                          <Icon className="h-4 w-4" style={{ color: accentColor }} />
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Filtros rápidos do dashboard</p>
                <p className="mt-1 text-sm text-slate-600">Refine o período, o canal e a empresa mantendo a mesma leitura contextual do módulo.</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { value: "7d", label: "7 dias" },
                { value: "30d", label: "30 dias" },
                { value: "90d", label: "90 dias" },
              ].map((option) => {
                const active = periodPreset === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPeriodPreset(option.value as "7d" | "30d" | "90d")}
                    className="rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors"
                    style={{
                      borderColor: active ? accentColor : "#cbd5e1",
                      backgroundColor: active ? `${accentColor}12` : "#ffffff",
                      color: active ? accentColor : "#475569",
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {isApsModule && (apsScopeType === "global" || apsScopeType === "channel") && channelOptions.length > 1 ? (
                <label className="grid gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Canal
                  <select
                    value={channelId ?? ""}
                    onChange={(event) => {
                      const nextValue = event.target.value ? Number(event.target.value) : undefined;
                      setChannelId(nextValue);
                      setCompanyId(undefined);
                    }}
                    className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none transition focus:border-slate-400"
                  >
                    <option value="">Todos os canais</option>
                    {channelOptions.map((channel: any) => (
                      <option key={String(channel.channelId)} value={String(channel.channelId)}>
                        {String(channel.channelName ?? `Canal ${channel.channelId}`)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {isApsModule && (apsScopeType === "global" || apsScopeType === "channel") && companyOptions.length > 1 ? (
                <label className="grid gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Empresa
                  <select
                    value={companyId ?? ""}
                    onChange={(event) => setCompanyId(event.target.value ? Number(event.target.value) : undefined)}
                    className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none transition focus:border-slate-400"
                  >
                    <option value="">Todas as empresas</option>
                    {companyOptions.map((company: any) => (
                      <option key={String(company.companyDimId)} value={String(company.companyId ?? company.companyDimId)}>
                        {String(company.companyName ?? `Empresa ${company.companyDimId}`)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {!isApsModule && showSharedCompanyFilter ? (
                <label className="grid gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Cliente
                  <select
                    value={companyId ?? ""}
                    onChange={(event) => setCompanyId(event.target.value ? Number(event.target.value) : undefined)}
                    className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none transition focus:border-slate-400"
                  >
                    <option value="">Todos os clientes</option>
                    {sharedCompanyOptions.map((company: any) => (
                      <option key={String(company.companyDimId ?? company.companyId)} value={String(company.companyId ?? company.companyDimId)}>
                        {String(company.tradeName ?? company.legalName ?? `Empresa ${company.companyId ?? company.companyDimId}`)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {!isApsModule && modalityOptions.length > 1 ? (
                <div className="grid gap-1 md:col-span-2 xl:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Modalidade</span>
                  <div className="flex flex-wrap gap-2 rounded-[1.1rem] border border-slate-200 bg-slate-50 p-2">
                    <button
                      type="button"
                      onClick={() => setCareModality(undefined)}
                      className="rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors"
                      style={{
                        borderColor: careModality ? "#cbd5e1" : accentColor,
                        backgroundColor: careModality ? "#ffffff" : `${accentColor}12`,
                        color: careModality ? "#475569" : accentColor,
                      }}
                    >
                      Todas
                    </button>
                    {modalityOptions.map((option) => {
                      const active = careModality === option;
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setCareModality(option)}
                          className="rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors"
                          style={{
                            borderColor: active ? accentColor : "#cbd5e1",
                            backgroundColor: active ? `${accentColor}12` : "#ffffff",
                            color: active ? accentColor : "#475569",
                          }}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2.5 text-xs text-slate-600">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium text-slate-700">
                {isApsModule ? summarizeCompactScope(apsScopeType) : `Leitura ativa: ${selectedSharedSectionLabel}`}
              </span>
              <a href="/portal/salvia" className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-950">
                Abrir hub de insights
              </a>
              {(periodPreset !== "30d" || careModality || companyId || channelId || selectedSection.id !== "visao-geral") ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 rounded-full border border-transparent px-3 py-1.5 font-medium text-slate-600 transition-colors hover:border-slate-200 hover:bg-white hover:text-slate-900"
                >
                  <RefreshCcw className="h-3.5 w-3.5" />
                  Voltar ao padrão
                </button>
              ) : null}
            </div>
          </div>
        </section>

        {isVisibleReadOnly ? (
          <Card className="rounded-[1.75rem] border border-amber-200 bg-amber-50 p-6 shadow-sm">
            <p className="text-base font-semibold text-amber-900">Este módulo está visível para consulta, mas suas ações operacionais permanecem limitadas no seu perfil.</p>
            <p className="mt-2 text-sm leading-6 text-amber-800">
              A visualização foi mantida porque o serviço faz parte do seu contexto atual. Se você precisar operar este módulo, a liberação depende do papel estrutural e das permissões vinculadas ao seu usuário.
            </p>
          </Card>
        ) : null}

        {error ? (
          <Card className="rounded-[1.75rem] border border-red-100 bg-red-50 p-6 text-center shadow-sm">
            <p className="text-base font-semibold text-red-700">Não foi possível carregar os indicadores deste módulo agora.</p>
            <p className="mt-2 text-sm leading-6 text-red-600">{error.message}</p>
          </Card>
        ) : null}

        {isApsModule ? (
          <div className="space-y-4">
            <Card className="rounded-[1.35rem] border border-slate-200 p-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Seção</span>
                  <span className="font-semibold text-slate-950">{selectedSection.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Contexto</span>
                  <span className="font-semibold text-slate-950">{apsScopeType ?? "protegido"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Cobertura</span>
                  <span className="font-semibold text-slate-950">{channelOptions.length} canais · {companyOptions.length} empresas</span>
                </div>
              </div>
            </Card>

            <ApsModulePanels
              activeSectionId={selectedSection.id}
              data={apsDashboard}
              accentColor={accentColor}
              moduleLabel={title}
              isLoading={isLoading}
            />
          </div>
        ) : (
          <>
            {isTelemedicineModule ? (
              <Card className="rounded-[1.35rem] border border-slate-200 p-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Seção</span>
                    <span className="font-semibold text-slate-950">{selectedSharedSectionLabel}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Escopo</span>
                    <span className="font-semibold text-slate-950">{context?.scopedCompanies ?? 0} empresas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Período</span>
                    <span className="font-semibold text-slate-950">{periodPreset === "7d" ? "7 dias" : periodPreset === "30d" ? "30 dias" : "90 dias"}</span>
                  </div>
                </div>
              </Card>
            ) : null}

            {!error && !isLoading && !hasAnyData ? (
              <Card className="rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-8 text-center shadow-sm">
                <p className="text-lg font-semibold text-slate-950">Este módulo ainda não tem volume suficiente para preencher todos os quadros.</p>
                <p className="mt-3 text-sm leading-7 text-slate-600">
                  Assim que a base de homologação ganhar mais volume, este módulo passa a preencher os indicadores automaticamente dentro da mesma estrutura e sempre mostram algo informativo enquanto a operação amadurece.
                </p>
              </Card>
            ) : null}

            <TelemedicineModulePanels
              activeSectionId={activeSectionId}
              overview={showTelemedicineOverview ? overview ?? null : null}
              timeline={showTelemedicineTimeline ? timelineQuery.data : []}
              breakdowns={showTelemedicineBreakdowns ? breakdowns ?? null : null}
              accentColor={accentColor}
              moduleLabel={title}
              isLoading={isLoading}
            />
          </>
        )}
        <ModuleAIChatWidget enabled={showHealthAssistant} />
      </div>
    </SalviaLayout>
  );
}
